import { motion } from 'framer-motion';
import { Check, Crown, LucideIcon, Star, Zap } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import NoiseTexture from 'src/pages/landing/components/NoiseTexture';
import BrandLogo from 'src/shared/components/BrandLogo';

interface UpgradeCelebrationScreenProps {
  onComplete: () => void;
}

const PERKS: { icon: LucideIcon; label: string }[] = [
  { icon: Zap, label: 'Unlimited research' },
  { icon: Star, label: 'Unlimited clients' },
  { icon: Crown, label: 'Early access to new features' },
];

const UpgradeCelebrationScreen: React.FC<UpgradeCelebrationScreenProps> = ({ onComplete }) => {
  const [fadeOut, setFadeOut] = useState(false);
  const [progressWidth, setProgressWidth] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setProgressWidth(100), 400);
    const t2 = setTimeout(() => {
      setFadeOut(true);
      setTimeout(onComplete, 800);
    }, 7500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [onComplete]);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-shadow overflow-hidden transition-opacity duration-800 ${fadeOut ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
    >
      <NoiseTexture />

      <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
          className="mb-5"
        >
          <BrandLogo variant="white" size="md" />
        </motion.div>

        <motion.div
          className="w-16 h-[1px] mb-7"
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
        >
          <motion.div
            className="w-full h-full bg-gradient-to-r from-transparent via-amber-400/30 to-transparent"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 3, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.6, rotate: -10 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 0.6, delay: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
          className="mb-6"
        >
          <motion.div
            className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400/15 to-amber-400/5 border border-amber-400/20 flex items-center justify-center mx-auto"
            animate={{
              boxShadow: [
                '0 0 20px rgba(251,191,36,0.05)',
                '0 0 40px rgba(251,191,36,0.12)',
                '0 0 20px rgba(251,191,36,0.05)',
              ],
            }}
            transition={{ duration: 3, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
          >
            <Crown className="w-7 h-7 text-amber-400" />
          </motion.div>
        </motion.div>

        <motion.h1
          className="text-3xl sm:text-4xl font-bold font-sans text-white tracking-tight mb-2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          Welcome to Professional
        </motion.h1>

        <motion.p
          className="text-base font-sans text-white/45 mb-10"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          Unlimited access unlocked
        </motion.p>

        <motion.div
          className="w-full max-w-xs bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 mb-12 backdrop-blur-sm"
          initial={{ opacity: 0, y: 16, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 1.6 }}
        >
          <div className="flex flex-col gap-3.5">
            {PERKS.map((perk, i) => {
              const PerkIcon = perk.icon;
              return (
                <motion.div
                  key={perk.label}
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.45, delay: 1.9 + i * 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  <motion.div
                    className="w-6 h-6 rounded-md bg-amber-400/10 border border-amber-400/15 flex items-center justify-center flex-shrink-0"
                    initial={{ scale: 0, rotate: -90 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.35, delay: 2.0 + i * 0.35, ease: [0.34, 1.56, 0.64, 1] }}
                  >
                    <PerkIcon className="w-3 h-3 text-amber-400" />
                  </motion.div>
                  <span className="text-sm font-sans font-medium text-white/75">{perk.label}</span>
                  <motion.div
                    className="ml-auto"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3, delay: 2.2 + i * 0.35, ease: [0.34, 1.56, 0.64, 1] }}
                  >
                    <Check className="w-4 h-4 text-emerald-400/70" />
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          className="w-full max-w-xs flex flex-col items-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 3.4 }}
        >
          <div className="w-full h-[2px] bg-white/[0.06] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all ease-out"
              style={{
                width: `${progressWidth}%`,
                transitionDuration: '6.5s',
                background: 'linear-gradient(90deg, rgba(251,191,36,0.5), rgba(251,191,36,0.15))',
              }}
            />
          </div>
          <motion.p
            className="text-xs font-sans font-medium tracking-widest uppercase text-white/25"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 2.5, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
          >
            Preparing your upgraded workspace
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
};

export default UpgradeCelebrationScreen;
